import type { APIUserStats } from "@rosette/ts/src/business/api/api_schema.js";
import type { StaticImageData } from "next/image";
import Image from "next/image";
import React from "react";
import { getRulesetIcon } from "@/components/icon/getRulesetIcon.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { getBotIcon } from "@/components/icon/getBotIcon.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { cn } from "@rosette/ts/src/util/cn.js";


interface Category {
    name: string;
    wins: number;
    losses: number;
    subCategories?: Category[];
}

function formatCategory({ name, wins, losses, subCategories }: Category): string {
    const games = wins + losses;
    let desc = `${games.toLocaleString()}\u00a0${name}`;
    if (subCategories && subCategories.length > 0) {
        const subDesc = formatCategories(subCategories);
        if (subDesc.length > 0) {
            desc += " (" + subDesc + ")";
        }
    }
    return desc;
}

function formatCategories(categories: Category[]): string {
    let desc = "";
    for (const category of categories) {
        const games = category.wins + category.losses;
        if (games > 0) {
            if (desc.length !== 0) {
                desc += ", ";
            }
            desc += formatCategory(category);
        }
    }
    return desc;
}


function calcTotals(categories: Category[]): {
    totalWins: number;
    totalGames: number;
} {
    let totalWins = 0;
    let totalGames = 0;
    for (const { wins, losses } of categories) {
        totalWins += wins;
        totalGames += wins + losses;
    }
    return { totalWins, totalGames };
}


interface GameCountBlockProps {
    icon: StaticImageData;
    iconAlt: string;
    name: string;
    categories: Category[];
}


function GameCountBlock({ icon, iconAlt, name, categories }: GameCountBlockProps) {
    categories = [...categories];
    categories.sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses));

    const { totalWins, totalGames } = calcTotals(categories);
    if (totalGames === 0)
        return (<></>);

    const desc = formatCategories(categories);
    return (
        <div className={cn(
            "flex w-full flex-col overflow-hidden rounded-2xl bg-page text-body shadow-md",
        )}>
            <div className="w-full bg-surface-sunken px-3 py-1 text-xl text-body">
                {name}
            </div>
            <div className="flex flex-row">
                <div className="flex flex-col items-center justify-start p-3 pr-0">
                    <Image className="w-[3em] max-w-[3em] min-w-[3em] object-contain" src={icon} alt={iconAlt} />
                </div>
                <div className="p-1 pl-0">
                    <p className="m-0 px-3 pt-1 text-strong">
                        {`Won ${totalWins.toLocaleString()} of ${totalGames.toLocaleString()} games`}
                    </p>
                    {desc.length > 0 && (
                        <p className="m-0 px-3 pt-1 text-subtle">
                            {desc}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}


interface AccountBasicStatsProps {
    stats: APIUserStats;
}


export function AccountBasicStats({ stats }: AccountBasicStatsProps) {
    const blocks: GameCountBlockProps[] = [
        {
            icon: getRulesetIcon(Ruleset.FINKEL),
            iconAlt: Ruleset.FINKEL.getDesc(),
            name: Ruleset.FINKEL.getName() + " Rules",
            categories: [
                {
                    name: "Online",
                    wins: stats.online_finkel_wins,
                    losses: stats.online_finkel_losses,
                    subCategories: [
                        {
                            name: "Ranked",
                            wins: stats.ranked_finkel_wins,
                            losses: stats.ranked_finkel_losses,
                        },
                    ],
                },
                {
                    name: "Friend",
                    wins: stats.friend_finkel_wins,
                    losses: stats.friend_finkel_losses,
                },
                {
                    name: "Computer",
                    wins: stats.computer_finkel_wins,
                    losses: stats.computer_finkel_losses,
                },
            ],
        },
        {
            icon: getRulesetIcon(Ruleset.BLITZ),
            iconAlt: Ruleset.BLITZ.getDesc(),
            name: Ruleset.BLITZ.getName() + " Rules",
            categories: [
                {
                    name: "Online",
                    wins: stats.online_blitz_wins,
                    losses: stats.online_blitz_losses,
                    subCategories: [
                        {
                            name: "Ranked",
                            wins: stats.ranked_blitz_wins,
                            losses: stats.ranked_blitz_losses,
                        },
                    ],
                },
                {
                    name: "Friend",
                    wins: stats.friend_blitz_wins,
                    losses: stats.friend_blitz_losses,
                },
                {
                    name: "Computer",
                    wins: stats.computer_blitz_wins,
                    losses: stats.computer_blitz_losses,
                },
            ],
        },
        {
            icon: getRulesetIcon(Ruleset.MASTERS),
            iconAlt: Ruleset.MASTERS.getDesc(),
            name: Ruleset.MASTERS.getName() + " Rules",
            categories: [
                {
                    name: "Online",
                    wins: stats.online_masters_wins,
                    losses: stats.online_masters_losses,
                    subCategories: [
                        {
                            name: "Ranked",
                            wins: stats.ranked_masters_wins,
                            losses: stats.ranked_masters_losses,
                        },
                    ],
                },
                {
                    name: "Friend",
                    wins: stats.friend_masters_wins,
                    losses: stats.friend_masters_losses,
                },
                {
                    name: "Computer",
                    wins: stats.computer_masters_wins,
                    losses: stats.computer_masters_losses,
                },
            ],
        },
        {
            icon: getBotIcon(BotType.PANDA),
            iconAlt: BotType.PANDA.getDesc(),
            name: BotType.PANDA.getLongName(),
            categories: [
                {
                    name: "Finkel",
                    wins: stats.panda_finkel_wins,
                    losses: stats.panda_finkel_losses,
                },
                {
                    name: "Blitz",
                    wins: stats.panda_blitz_wins,
                    losses: stats.panda_blitz_losses,
                },
                {
                    name: "Masters",
                    wins: stats.panda_masters_wins,
                    losses: stats.panda_masters_losses,
                },
            ],
        },
    ];

    // Filter and sort
    const blocksWithGames = blocks.filter((props) => {
        const { totalGames } = calcTotals(props.categories);
        return totalGames > 0;
    });
    blocksWithGames.sort((a, b) => {
        const aWins = calcTotals(a.categories).totalWins;
        const bWins = calcTotals(b.categories).totalWins;
        return bWins - aWins;
    });

    return (
        <div className="flex flex-col items-start gap-3 md:flex-row">
            <div className="flex flex-1 flex-col items-start gap-3 text-base">
                {blocksWithGames.map((props, index) => (index % 2 === 0) && (
                    <GameCountBlock key={props.name} {...props} />
                ))}
            </div>
            <div className="flex flex-1 flex-col items-start gap-3 text-base">
                {blocksWithGames.map((props, index) => (index % 2 === 1) && (
                    <GameCountBlock key={props.name} {...props} />
                ))}
            </div>
        </div>
    );
}
